import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { getModels, createModel, updateModel, deleteModel, getBrands, uploadFile, getFileUrl, getActiveBrand } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Plus, Trash, Car, PencilSimple, UploadSimple, Link as LinkIcon } from '@phosphor-icons/react';
import { toast } from 'sonner';

const formatDZD = (n) => {
  if (n === null || n === undefined || n === '' || isNaN(Number(n))) return '—';
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Number(n)) + ' DZD';
};

const buildImageSrc = (imageUrl) => {
  if (!imageUrl) return null;
  if (imageUrl.startsWith('http')) return imageUrl;
  return getFileUrl(imageUrl);
};

const ModelsPage = () => {
  const { t } = useTranslation();
  const [models, setModels] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterBrand, setFilterBrand] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingModel, setEditingModel] = useState(null);
  const [formData, setFormData] = useState({ brand_id: '', name: '', years: '', image_url: '', variants: [] });
  const [uploading, setUploading] = useState(false);
  const [catalogSlug, setCatalogSlug] = useState('');
  const fileInputRef = useRef(null);

  // Fetch this tenant's slug once on mount so the "Copy Public Catalog Link"
  // button knows what URL to copy.
  useEffect(() => {
    let cancelled = false;
    getActiveBrand()
      .then((res) => { if (!cancelled) setCatalogSlug(res.data?.company_slug || ''); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const copyCatalogLink = () => {
    if (!catalogSlug) {
      toast.error('Your company slug is not configured yet — contact support.');
      return;
    }
    const url = `${window.location.origin}/catalog/${catalogSlug}`;
    navigator.clipboard.writeText(url).then(
      () => toast.success(`Copied: ${url}`),
      () => toast.error('Could not copy — please copy manually: ' + url),
    );
  };

  const loadData = useCallback(async () => {
    try {
      const [modelRes, brandRes] = await Promise.all([
        getModels({ brand_id: filterBrand === 'all' ? undefined : filterBrand }),
        getBrands()
      ]);
      setModels(modelRes.data);
      setBrands(brandRes.data);
    } catch (err) {
      console.error('Failed to load models/brands', err);
    } finally {
      setLoading(false);
    }
  }, [filterBrand]);

  useEffect(() => { loadData(); }, [loadData]);

  const resetForm = useCallback(() => {
    setEditingModel(null);
    setFormData({ brand_id: '', name: '', years: '', image_url: '', variants: [] });
  }, []);

  const openCreate = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEdit = (model) => {
    setEditingModel(model);
    setFormData({
      brand_id: model.brand_id || '',
      name: model.name || '',
      years: (model.years || []).join(', '),
      image_url: model.image_url || '',
      variants: (model.variants || []).map(v => ({
        id: v.id,
        name: v.name || '',
        price: v.price ?? '',
        is_active: v.is_active !== false,
      })),
    });
    setDialogOpen(true);
  };

  const addVariant = () => {
    setFormData(prev => ({
      ...prev,
      variants: [...(prev.variants || []), { name: '', price: '', is_active: true }],
    }));
  };
  const updateVariant = (idx, patch) => {
    setFormData(prev => ({
      ...prev,
      variants: prev.variants.map((v, i) => (i === idx ? { ...v, ...patch } : v)),
    }));
  };
  const removeVariant = (idx) => {
    setFormData(prev => ({
      ...prev,
      variants: prev.variants.filter((_, i) => i !== idx),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const yearsArray = formData.years
        ? formData.years.split(',').map(y => parseInt(y.trim(), 10)).filter(y => !isNaN(y))
        : [];
      const cleanVariants = (formData.variants || [])
        .map(v => ({
          ...(v.id ? { id: v.id } : {}),
          name: (v.name || '').trim(),
          price: v.price === '' || v.price === null || v.price === undefined ? null : Number(v.price),
          is_active: v.is_active !== false,
        }))
        .filter(v => v.name); // drop empty rows
      const payload = {
        brand_id: formData.brand_id,
        name: formData.name,
        years: yearsArray,
        image_url: formData.image_url,
        variants: cleanVariants,
      };
      if (editingModel) {
        await updateModel(editingModel.id, payload);
        toast.success('Model updated');
      } else {
        await createModel(payload);
        toast.success('Model created');
      }
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save model');
    }
  };

  const handleDelete = async (model) => {
    if (!window.confirm(`Delete model "${model.name}"?`)) return;
    try {
      await deleteModel(model.id);
      toast.success('Model deleted');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete model');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5 MB');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('category', 'model_image');
      const res = await uploadFile(fd);
      setFormData((prev) => ({ ...prev, image_url: res.data.id }));
      toast.success('Image uploaded');
    } catch (err) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const getBrandName = (brandId) => {
    const brand = brands.find(b => b.id === brandId);
    return brand?.name || 'Unknown';
  };

  return (
    <div data-testid="models-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t('nav.models')}</h1>
          <p className="text-muted-foreground text-sm">Manage car models, trims & prices. Models with at least one priced variant appear on your public catalog.</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={copyCatalogLink}
            data-testid="copy-catalog-link-btn"
            title={catalogSlug ? `${window.location.origin}/catalog/${catalogSlug}` : 'Set a slug in Companies first'}
          >
            <LinkIcon className="w-4 h-4 mr-2" /> Copy Public Catalog Link
          </Button>
          <Button onClick={openCreate} className="btn-primary" data-testid="add-model-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Model
          </Button>
        </div>
      </div>

      <div className="mb-6">
        <Select value={filterBrand} onValueChange={setFilterBrand}>
          <SelectTrigger className="w-48 input-brutalist" data-testid="filter-brand">
            <SelectValue placeholder="All Brands" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Brands</SelectItem>
            {brands.map((brand) => (
              <SelectItem key={brand.id} value={brand.id}>{brand.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : models.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          {filterBrand !== 'all' ? 'No models for this brand' : 'No models yet. Add your first car model!'}
        </div>
      ) : (
        <div className="border border-border overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-secondary/50">
                <th className="table-header w-20">Image</th>
                <th className="table-header">{t('vehicles.brand')}</th>
                <th className="table-header">{t('vehicles.model')}</th>
                <th className="table-header hidden sm:table-cell">Available Years</th>
                <th className="table-header">Price (from)</th>
                <th className="table-header hidden md:table-cell">Suppliers</th>
                <th className="table-header text-right">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {models.map((model, idx) => {
                const imgSrc = buildImageSrc(model.image_url);
                const activeVariants = (model.variants || []).filter(v => v.is_active !== false && v.price);
                const prices = activeVariants.map(v => Number(v.price)).filter(p => !isNaN(p) && p > 0);
                const priceFrom = prices.length ? Math.min(...prices) : null;
                const variantCount = (model.variants || []).length;
                return (
                  <tr key={model.id} className={`animate-fade-in stagger-${(idx % 5) + 1}`} data-testid={`model-row-${model.id}`}>
                    <td className="table-cell">
                      {imgSrc ? (
                        <img src={imgSrc} alt={model.name} className="w-14 h-10 object-contain bg-secondary" />
                      ) : (
                        <div className="w-14 h-10 bg-secondary flex items-center justify-center">
                          <Car weight="duotone" className="w-5 h-5 text-muted-foreground" />
                        </div>
                      )}
                    </td>
                    <td className="table-cell">{getBrandName(model.brand_id)}</td>
                    <td className="table-cell font-medium">{model.name}</td>
                    <td className="table-cell hidden sm:table-cell text-muted-foreground font-mono text-sm">
                      {model.years?.length > 0 ? model.years.join(', ') : '-'}
                    </td>
                    <td className="table-cell">
                      {priceFrom !== null ? (
                        <div>
                          <div className="font-semibold text-sm">{formatDZD(priceFrom)}</div>
                          <div className="text-[11px] text-muted-foreground">{variantCount} variant{variantCount === 1 ? '' : 's'}</div>
                        </div>
                      ) : (
                        <span className="text-sm text-muted-foreground">No price set</span>
                      )}
                    </td>
                    <td className="table-cell hidden md:table-cell">
                      {model.supplier_count > 0 ? (
                        <span className="text-sm text-green-600 font-medium">{model.supplier_count} available</span>
                      ) : (
                        <span className="text-sm text-muted-foreground">No suppliers</span>
                      )}
                    </td>
                    <td className="table-cell text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(model)} data-testid={`edit-model-${model.id}`} title="Edit">
                          <PencilSimple className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(model)} className="text-destructive" data-testid={`delete-model-${model.id}`} title="Delete">
                          <Trash className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingModel ? 'Edit Model' : 'Add Model'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>{t('vehicles.brand')} *</Label>
              <Select value={formData.brand_id} onValueChange={(v) => setFormData({ ...formData, brand_id: v })}>
                <SelectTrigger className="input-brutalist" data-testid="model-brand-select">
                  <SelectValue placeholder="Select brand" />
                </SelectTrigger>
                <SelectContent>
                  {brands.map((brand) => (
                    <SelectItem key={brand.id} value={brand.id}>{brand.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Model Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Camry"
                required
                className="input-brutalist"
                data-testid="model-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Available Years (comma-separated)</Label>
              <Input
                value={formData.years}
                onChange={(e) => setFormData({ ...formData, years: e.target.value })}
                placeholder="e.g., 2022, 2023, 2024"
                className="input-brutalist"
              />
            </div>
            <div className="space-y-2">
              <Label>Model Picture</Label>
              {formData.image_url && (
                <div className="mb-2 flex items-center gap-3 p-2 border border-border">
                  <img src={buildImageSrc(formData.image_url)} alt="Preview" className="w-20 h-14 object-contain bg-secondary" />
                  <button type="button" onClick={() => setFormData({ ...formData, image_url: '' })} className="text-xs text-destructive hover:underline">Remove</button>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/webp"
                onChange={handleImageUpload}
                className="hidden"
                id="model-image-input"
                data-testid="model-image-file"
              />
              <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading} className="w-full">
                <UploadSimple className="w-4 h-4 mr-2" />
                {uploading ? 'Uploading...' : (formData.image_url ? 'Replace Picture' : 'Upload Picture')}
              </Button>
              <p className="text-[11px] text-muted-foreground">PNG / JPG / WebP, max 5 MB.</p>
            </div>

            <div className="space-y-2 pt-2 border-t border-border">
              <div className="flex items-center justify-between">
                <Label>Variants & Prices</Label>
                <Button type="button" variant="ghost" size="sm" onClick={addVariant} data-testid="add-variant-btn">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add variant
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground">
                Add one row per trim or year. Variants with a price are shown on the public catalog.
              </p>
              {(formData.variants || []).length === 0 ? (
                <p className="text-xs text-muted-foreground italic py-2">No variants yet — click &quot;Add variant&quot; to set a price.</p>
              ) : (
                <div className="space-y-2">
                  {formData.variants.map((v, i) => (
                    <div key={i} className="grid grid-cols-[1fr_140px_auto] gap-2 items-center">
                      <Input
                        placeholder="Trim / variant name (e.g. BASIC 2026)"
                        value={v.name}
                        onChange={(e) => updateVariant(i, { name: e.target.value })}
                        data-testid={`variant-name-${i}`}
                      />
                      <Input
                        type="number"
                        inputMode="numeric"
                        placeholder="Price (DZD)"
                        value={v.price}
                        onChange={(e) => updateVariant(i, { price: e.target.value })}
                        data-testid={`variant-price-${i}`}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeVariant(i)}
                        className="text-destructive"
                        data-testid={`variant-delete-${i}`}
                      >
                        <Trash className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
              <Button type="submit" className="btn-primary" data-testid="model-submit-btn">{t('common.save')}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ModelsPage;
